import type { AxiosResponse } from "axios";
import type {
	FilterParams,
	MetadataTypes,
	ResponseRead,
	ResponseReadAll,
} from "epsm-api-types";
import type { IConverter } from "epsm-api-types/converter";
import { prepareData, prepareItem } from "@/lib/utils";
import type { IReadAllParams } from "./RequestParams";

export abstract class BaseService<TItem, TItemDTO> {
	protected constructor(
		protected readonly converter: IConverter<TItem, TItemDTO>,
	) {}

	getOptimizedFilter(filters?: FilterParams): FilterParams {
		if (!filters?.length) return [];

		return filters.map((filter) => {
			if (!("name" in filter) || !("condition" in filter)) return filter;

			if (
				filter.condition === "in" &&
				Array.isArray(filter.value) &&
				filter.value.length === 1
			) {
				return {
					...filter,
					condition: "eq",
					value: filter.value[0],
				};
			}

			return filter;
		});
	}

	protected getBodyParams(params: Readonly<IReadAllParams>) {
		return {
			params: {
				...(params.pagination && { pagination: params.pagination }),
				...(params.total && { total: params.total }),
				...(params.sort?.length && { sort: params.sort }),
				...(params.fields?.length && { fields: params.fields }),
				...(params.filter?.length && {
					filter: this.getOptimizedFilter(params.filter),
				}),
				...(params.custom?.length && { custom: params.custom }),
				...(params.lookup && { lookup: params.lookup }),
			},
			metadata: {
				...(params.types && { types: params.types }),
			},
		};
	}

	protected getQueryParams(params: Readonly<IReadAllParams>) {
		if (params?.custom?.length) {
			throw new Error("Custom fields are not supported, use POST request");
		}

		const filters: Record<string, string> = {};

		if (params?.filter?.length) {
			const optimizedFilters = this.getOptimizedFilter(params.filter);

			for (const filter of optimizedFilters) {
				if ("name" in filter) {
					const filterString = `${filter.condition}:${filter.value}${"options" in filter ? `:${filter.options}` : ""}`;

					if (filters[filter.name]) {
						filters[filter.name] += `,${filterString}`;
					} else {
						filters[filter.name] = filterString;
					}
				}
			}
		}

		const result: Record<string, string> = {
			...(params?.pagination?.limit && {
				pagination: `${params.pagination.limit},${params.pagination.offset ?? 0}`,
			}),
			...(params?.total && { total: params.total }),
			...(params?.sort?.length && {
				sort: params.sort
					.map((item) => `${item.order > 0 ? "+" : "-"}${item.name}`)
					.join(","),
			}),
			...(params?.fields?.length && { fields: params.fields.join(",") }),
			...(params?.types && {
				types: Object.entries(params.types)
					.map(([key, value]) => `${key}:${value}`)
					.join(","),
			}),
			...(params?.lookup && {
				lookup: `${params.lookup.from}:${params.lookup.localField}:${params.lookup.foreignField}`,
			}),
			...filters,
		};
		return result;
	}

	protected getResponseReadAll<TResult = TItem>(
		response: AxiosResponse<ResponseReadAll<TItemDTO>>,
		types?: MetadataTypes,
		mapper?: (item: TItem) => TResult,
	): ResponseReadAll<TResult> {
		return {
			...response.data,
			data: prepareData(this.converter, response.data.data, types, mapper),
		};
	}

	protected getResponseItem<TResult = TItem>(
		response: AxiosResponse<ResponseRead<TItemDTO>>,
		types?: MetadataTypes,
		mapper?: (item: TItem) => TResult,
	): ResponseRead<TResult> {
		return {
			...response.data,
			data: prepareItem(this.converter, response.data.data, types, mapper),
		};
	}
}
